import { FavoriteRemove, Home, HomeFill, Icon, IconSize, Menu, MenuItem, MoreVertical } from '@soteria/design-system-ve';

import * as styles from './Guide.css';

/**
 * Props for the FavoriteMenu component
 * @interface FavoriteMenuProps
 */
interface FavoriteMenuProps {
  /** Unique identifier for the favorite item */
  favoriteId: string;
  /** Display label for the favorite (used in aria-label) */
  favoriteLabel: string;
  /** Whether the menu is currently open */
  isOpen: boolean;
  /** Whether this favorite is currently set as the home page */
  isHomePage: boolean;
  /** Callback when the menu open state changes */
  onOpenChange: (isOpen: boolean) => void;
  /** Callback when user wants to set this favorite as home page */
  onSetAsHome: () => void;
  /** Callback when user wants to clear home page setting */
  onClearHome: () => void;
  /** Callback when user wants to remove this favorite */
  onRemove: () => void;
}

/**
 * FavoriteMenu component
 * Provides action menu for favorite items (Set as Home, Remove from Favorites)
 */
export function FavoriteMenu({ favoriteId, favoriteLabel, isOpen, isHomePage, onOpenChange, onSetAsHome, onClearHome, onRemove }: FavoriteMenuProps) {
  // Sanitize favoriteId to ensure valid HTML id (replace whitespace and special chars with hyphens)
  const sanitizeId = (id: string) => id.replace(/[\s]+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');
  const triggerId = `favorite-menu-trigger-${sanitizeId(favoriteId)}`;

  const handleHomeClick = () => {
    if (isHomePage) {
      onClearHome();
    } else {
      onSetAsHome();
    }

    onOpenChange(false);
  };

  const handleRemove = () => {
    onRemove();
    onOpenChange(false);
  };

  return (
    <>
      <button
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={`More actions for ${favoriteLabel}`}
        className={styles.groupMenuButton}
        id={triggerId}
        type="button"
        onClick={e => {
          e.stopPropagation();
          onOpenChange(!isOpen);
        }}
      >
        <Icon icon={MoreVertical} iconSize={IconSize.XSmall} />
      </button>

      {isOpen && (
        <Menu isOpen showCloseButton={false} triggerId={triggerId} onOpenChange={onOpenChange}>
          <MenuItem
            icon={<Icon icon={isHomePage ? HomeFill : Home} iconSize={IconSize.XSmall} />}
            label={isHomePage ? 'Clear Home Page' : 'Set as Home Page'}
            onClick={handleHomeClick}
          />
          <MenuItem icon={<Icon icon={FavoriteRemove} iconSize={IconSize.XSmall} />} label="Remove from Favorites" onClick={handleRemove} />
        </Menu>
      )}
    </>
  );
}
